import { ELEMENT_PARAGRAPH } from '@doltech/utils/lib/constants';
import { isEqual } from 'lodash';
import { nanoid } from 'nanoid';
import normalizeWhitespace from 'normalize-html-whitespace';
import { Editor, Element, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { ELEMENT_TABLE, ELEMENT_TR } from '../../../../slatePlugins';
import { isBlockAboveEmpty } from '../../../../slatePlugins/common/queries/isBlockAboveEmpty';
import { getInlineTypes } from '../../../../slatePlugins/components/core/utils/getInlineTypes';
import { deserializeHTMLToDocumentFragment } from '../../../../slatePlugins/deseriailzers/utils/deserializeHTMLToDocumentFragment';
import { BLANK_TYPE } from '../../../BlankEditor/type';
import {
  ELEMENT_BULLET_POINT,
  ELEMENT_BULLET_POINT_CHECK,
  ELEMENT_BULLET_POINT_LIST_ITEM,
  ELEMENT_BULLET_POINT_NUMBER,
  ELEMENT_BULLET_POINT_QUESTION,
  ELEMENT_BULLET_POINT_X,
} from '../../../BlogEditor/plugins/BulletPoint/BulletPointPlugin.types';
import { VOCAB_CARD } from '../../../BlogEditor/plugins/CardLogic/model/constants';
import { serializeNodeToArray } from '../../../BlogEditor/plugins/shared-transforms';
import { handleUploadWithApi } from '../../../FileUpload/fileUploadUtils';

// import { isEmptyTextNode } from '../../helpers/node-filter';
const HTML_CONTENT = 'text/html';

const CANT_MERGE_NODE_TYPES = [
  ELEMENT_TR,
  ELEMENT_BULLET_POINT,
  ELEMENT_BULLET_POINT_CHECK,
  ELEMENT_BULLET_POINT_X,
  ELEMENT_BULLET_POINT_QUESTION,
  ELEMENT_BULLET_POINT_NUMBER,
  ELEMENT_BULLET_POINT_LIST_ITEM,
  ELEMENT_TABLE,
];

export const normalizeDivFragment = (fragments) => {
  const result = [];
  fragments.forEach((fragment, fragmentIndex) => {
    // Don't add \n only node (which cause crash)
    if (typeof fragment.type === 'undefined' && fragment.text === '\n') {
      // if fragmentIndex in the middle, then append \n
      if (fragmentIndex > 0 && fragmentIndex < fragments.length - 1) {
        result.push({ text: '\n' });
      }
      return;
    }
    if (fragment.type === 'div') {
      const nodeText = serializeNodeToArray(fragment).join('');

      // Don't add empty text node (bloated value on editor)
      if (nodeText.trim().length === 0) {
        return;
      }

      // normalize div fragment - flatten div nested
      fragment.type = ELEMENT_PARAGRAPH;
      const firstChild = fragment?.children?.[0];
      if (firstChild?.type === 'div') {
        result.push(normalizeDivFragment(fragment.children));
        return;
      }
    }

    if (fragment.children instanceof Array && fragment.children.length) {
      // join whitespace
      const fragmentChildren = [];
      let currNode = fragment.children[0];
      for (let i = 1; i < fragment.children.length; i++) {
        const nextChild = fragment.children[i];
        // current node and nextNode have same properties -> merge
        // only whitespace -> merge
        const isSameNode = isEqual(Object.keys(currNode), Object.keys(nextChild));
        const canJoin =
          (currNode.text && nextChild?.text?.trim()?.length === 0) ||
          (isSameNode && !CANT_MERGE_NODE_TYPES.includes(currNode?.type));
        if (canJoin) {
          currNode.text += nextChild.text;
        } else {
          fragmentChildren.push(currNode);
          currNode = nextChild;
        }
      }
      fragmentChildren.push(currNode);
      fragment.children = fragmentChildren;
    }
    result.push(fragment);
  });
  return result.map((fragment) => {
    if (fragment instanceof Array) {
      return fragment[0];
    }
    return fragment;
  });
};

const IGNORE_PRE_INSERT_ELMS = [
  ELEMENT_BULLET_POINT,
  ELEMENT_BULLET_POINT_CHECK,
  ELEMENT_BULLET_POINT_X,
  ELEMENT_BULLET_POINT_QUESTION,
  ELEMENT_BULLET_POINT_NUMBER,
  ELEMENT_BULLET_POINT_LIST_ITEM,
  ELEMENT_TABLE,
];

export const withDeserializeHTML =
  ({ plugins = [], ...options }: any = {}) =>
  <T extends ReactEditor>(editor: T) => {
    const { insertData } = editor;
    const {
      normalizeFragment = (fragment) => fragment,
      preInsert = (fragments) => {
        const inlineTypes = getInlineTypes(plugins);
        const firstNodeType = fragments[0].type as string | undefined;
        // don't replace table - table have custom deserialize for copy/paste
        if (IGNORE_PRE_INSERT_ELMS.includes(firstNodeType)) {
          return fragments;
        }
        fragments.forEach((fragment, index, theArray) => {
          if (fragment?.type === 'table') {
            const fragTR = fragment.children.find((item) => item.type === 'tr');
            const fragTD = fragTR.children.find((item) => item.type === 'td');
            const fragUpdate = fragTD.children;
            theArray[index] = [
              {
                type: ELEMENT_PARAGRAPH,
                id: nanoid(),
                children: fragUpdate,
              },
            ];
          }
        });
        // replace the selected node type by the first block type
        if (isBlockAboveEmpty(editor) && firstNodeType && !inlineTypes.includes(firstNodeType)) {
          Transforms.setNodes(editor, { type: fragments[0].type } as any);
        }
        const nodeEntries = Editor.nodes(editor, {
          at: editor.selection,
        });
        for (const [node] of nodeEntries) {
          if (Element.isElement(node) && (node as any).type === VOCAB_CARD) {
            Transforms.insertText(editor, fragments[0].text as string, {
              at: editor.selection,
            });
            return undefined;
          }
        }

        return normalizeDivFragment(normalizeFragment(fragments, editor));
      },

      insert = (fragment) => {
        if (fragment) {
          try {
            Transforms.insertFragment(editor, fragment);
            // eslint-disable-next-line no-empty
          } catch (ex) {
            console.log(ex);
          }
        }
      },

      normalizeHtml = (html) => {
        return normalizeWhitespace(html);
      },
    } = options;

    editor.insertData = (data: DataTransfer) => {
      let html: any = data;
      try {
        html = data.getData(HTML_CONTENT);
        // eslint-disable-next-line no-empty
      } catch (ex) {}
      if (html) {
        const normalizedHtml = normalizeHtml(html);
        const { body } = new DOMParser().parseFromString(normalizedHtml, HTML_CONTENT);

        // check if copy/paste from word
        // usually the google doc word wrap with b, we need to remove it
        const existRedudantBold: any = body.querySelector('[id^="docs-internal-guid"]');
        if (existRedudantBold?.childNodes) {
          existRedudantBold.replaceWith(...existRedudantBold.childNodes);
        }

        let fragment = deserializeHTMLToDocumentFragment({
          plugins,
          element: body,
        });
        fragment = preInsert(fragment) as any;
        insert(fragment);
        return;
      }

      const isUploadFile = data?.files?.length > 0;
      if (isUploadFile) {
        const file = data.files[0] as any;
        if (file.type.includes('image')) {
          // const f: any = new File([url], 'image.png', { type: 'image/png' });
          handleUploadWithApi({
            file,
            usePrivate: false,
          })
            .then((res) => {
              const image = {
                isVoid: true,
                id: nanoid(),
                children: [
                  {
                    text: '',
                  },
                ],
                type: 'IMAGE',
                fileInfo: res?.file,
              };
              Transforms.insertNodes(editor, image);
            })
            .catch((err) => {});
        }
      }
      insertData(data);
    };

    return editor;
  };

export const preprocessorSlateNode = (elements) => {
  let idx = 1;
  elements.forEach((el, index, els) => {
    if (el.text === '_____') {
      els[index] = {
        type: BLANK_TYPE,
        blankId: idx,
        children: [
          {
            blankId: idx,
            text: '',
          },
        ],
      };
      idx++;
    }
  });
};

export const htmlToSlate = (plugins, html: string) => {
  const data = html.replace(/\n/g, '<br />');
  const { body } = new DOMParser().parseFromString(normalizeWhitespace(data), HTML_CONTENT);
  const fragment = deserializeHTMLToDocumentFragment({
    plugins,
    element: body,
  });
  const res = normalizeDivFragment(fragment) as any;
  preprocessorSlateNode(res);
  return [
    {
      type: ELEMENT_PARAGRAPH,
      children: res,
    },
  ];
};
